import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { CategoryTabs } from '@/components/home/category-tabs'
import { PostCard } from '@/components/home/post-card'
import { SchoolSelector } from '@/components/home/school-selector'
import { SkeletonList } from '@/components/ui/skeleton-card'
import { useContentStore } from '@/stores/content-store'
import { useAuthStore } from '@/stores/auth-store'
import { PageTransition, EmptyState } from '@/components/ui/ux-enhancer'
import { useToast } from '@/components/ui/toast-provider'
import { Search } from 'lucide-react'

export default function HomePage() {
  const navigate = useNavigate()
  const [selectedCategory, setSelectedCategory] = useState('全部')
  const [selectedSchool, setSelectedSchool] = useState('全部学校')
  const { posts, fetchPosts, isLoading } = useContentStore()
  const { user } = useAuthStore()
  const { addToast } = useToast()

  // 处理分类切换
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category)
  }

  useEffect(() => {
    fetchPosts()
  }, [fetchPosts])

  // 欢迎提示由 hero 横幅承担（"欢迎回来，XX"），不再弹 toast，避免与登录成功通知重复

  // 时间格式化函数
  function formatTime(dateString: string): string {
    const now = new Date()
    const postTime = new Date(dateString)
    const diffInMinutes = Math.floor((now.getTime() - postTime.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return '刚刚'
    if (diffInMinutes < 60) return `${diffInMinutes}分钟前`
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours}小时前`
    
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays}天前`
    
    return postTime.toLocaleDateString('zh-CN')
  }

  // 处理真实发布的内容（分类已是后端中文枚举，直接透传）
  const allPosts = posts.map(post => ({
    id: post.id,
    userId: post.userId,
    author: {
      name: post.isAnonymous ? '匿名用户' : (post.user?.username || '未知用户'),
      avatar: post.isAnonymous ? '' : (post.user?.avatar || ''),
      school: post.school,
      level: post.user?.level || 1
    },
    user: {
      name: post.isAnonymous ? '匿名用户' : (post.user?.username || '未知用户'),
      avatar: post.isAnonymous ? '' : (post.user?.avatar || ''),
      school: post.school,
      level: post.user?.level || 1
    },
    content: post.content,
    title: post.title,
    images: post.images || [],
    category: post.category,
    createdAt: new Date(post.createdAt),
    time: formatTime(post.createdAt),
    likes: post.likes || 0,
    comments: post.comments || 0,
    location: post.location,
    price: post.price,
    condition: post.condition,
    tags: post.tags || [],
    isHot: (post.likes || 0) > 50,
    hot: (post.likes || 0) > 50
  }))

  // 筛选帖子
  const filteredPosts = allPosts.filter(post => {
    // 分类筛选逻辑
    const categoryMatch = selectedCategory === '全部' || post.category === selectedCategory
    
    // 学校筛选逻辑
    const schoolMatch = selectedSchool === '全部学校' || 
                       post.author?.school === selectedSchool || 
                       post.user?.school === selectedSchool
    
    return categoryMatch && schoolMatch
  })

  if (isLoading) {
    return (
      <PageTransition>
        <div className="space-y-4 lg:space-y-6">
          {/* 校区选择器 */}
          <div className="mb-4">
            <SchoolSelector 
              selectedSchool={selectedSchool}
              onSchoolChange={setSelectedSchool}
            />
          </div>
          
          <CategoryTabs 
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
            showAsLinks={false}
          />

          {/* 骨架屏加载状态 */}
          <SkeletonList count={5} />
        </div>
      </PageTransition>
    )
  }

  return (
    <PageTransition>
      <div className="space-y-4 lg:space-y-6">
        {/* 欢迎横幅 */}
        <div className="hero-gradient rounded-2xl p-6 lg:p-8 text-white shadow-lg relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full" />
          <div className="absolute -bottom-16 -left-8 w-52 h-52 bg-white/10 rounded-full" />
          <div className="relative">
            <h1 className="text-2xl lg:text-3xl font-bold mb-2">
              {user ? `欢迎回来，${user.nickname || user.username}！` : '欢迎来到大悟万能墙'}
            </h1>
            <p className="text-white/85 text-sm lg:text-base leading-relaxed">
              {user
                ? '校园里总有新鲜事，看看大家分享了什么吧'
                : '分享校园生活、学习互助、二手交易、失物招领，登录后即可发布'}
            </p>
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => navigate('/publish')}
                className="px-4 py-2 bg-white text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition-colors shadow-sm text-sm"
              >
                ✏️ 发布动态
              </button>
              {!user && (
                <button
                  onClick={() => navigate('/login')}
                  className="px-4 py-2 bg-white/15 text-white font-medium rounded-lg hover:bg-white/25 transition-colors text-sm border border-white/30"
                >
                  登录 / 注册
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 校区选择器 */}
        <div className="mb-4">
          <SchoolSelector 
            selectedSchool={selectedSchool}
            onSchoolChange={setSelectedSchool}
          />
        </div>
        
        <CategoryTabs 
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          showAsLinks={false}
        />

        {/* 内容区域 */}
        <div className="space-y-4 lg:space-y-6">
          {filteredPosts.length > 0 ? (
            filteredPosts.map(post => (
              <PostCard key={post.id} post={post} />
            ))
          ) : (
            <EmptyState
              icon={<Search className="w-16 h-16 text-gray-300" />}
              title="暂无相关内容"
              description={`当前筛选条件下没有找到内容，试试其他分类或学校吧`}
              action={
                <button
                  onClick={() => {
                    navigate('/publish')
                    addToast({
                      type: 'info',
                      title: '开始创作',
                      description: '分享你的校园生活吧！'
                    })
                  }}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  发布第一条动态
                </button>
              }
            />
          )}
        </div>
      </div>
    </PageTransition>
  )
}